import { useCallback, useEffect, useRef, useState } from "react";

const ACTIVITY_EVENTS = ["mousemove", "keydown", "click", "scroll", "touchstart"];
const HEARTBEAT_THROTTLE_MS = 1000; // max 1 broadcast per second per tab

/**
 * Tracks *system-wide* user activity: any DOM event in this tab OR a
 * heartbeat from a sibling tab counts as activity. After `idleMs` of
 * no activity anywhere in the browser, `isIdle` flips to true.
 *
 * This complements `useIdleTimer` (which is per-tab). The per-tab timer
 * catches "user abandoned this tab but is using a sibling" — that tab
 * will fire on its own 20-min mark. The system timer catches "user
 * walked away from the entire browser" — when no tab sees any activity
 * AND no sibling has sent a heartbeat recently, all tabs go idle
 * together.
 *
 * The hook owns its own BroadcastChannel for heartbeats (separate
 * from the main session channel) and broadcasts a throttled
 * `SYSTEM_ACTIVITY` message on every local DOM event. It listens for
 * the same message type from siblings and updates a remote-activity
 * timestamp.
 *
 * Returns:
 *   isIdle         — boolean, true when no local OR remote activity
 *                    for > idleMs
 *   resetIdleTimer — function to manually reset both local and
 *                    remote activity timestamps
 */
export const useSystemIdleTimer = ({ idleMs, events = ACTIVITY_EVENTS, channelName }) => {
  const [isIdle, setIsIdle] = useState(false);

  // Refs hold the latest values without re-triggering effects.
  // Initialized lazily in a setState-like ref pattern to keep the
  // initial value (Date.now) out of the render body, satisfying
  // react-hooks/purity.
  const localLastActivityRef = useRef(null);
  const remoteLastActivityRef = useRef(null);
  const isIdleRef = useRef(false);
  const lastBroadcastAtRef = useRef(0);
  const channelRef = useRef(null);

  // Lazy init effect — runs once on mount, sets the timestamps to
  // "now" outside the render body.
  useEffect(() => {
    if (localLastActivityRef.current == null) {
      localLastActivityRef.current = Date.now();
    }
    if (remoteLastActivityRef.current == null) {
      remoteLastActivityRef.current = Date.now();
    }
  }, []);

  // Periodic check timer — sees if we've been idle past the threshold.
  const checkTimerRef = useRef(null);

  const broadcastActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastBroadcastAtRef.current < HEARTBEAT_THROTTLE_MS) return;
    lastBroadcastAtRef.current = now;
    try {
      channelRef.current?.postMessage({ type: "SYSTEM_ACTIVITY", at: now });
    } catch {
      // broadcast failure is non-fatal
    }
  }, []);

  const updateLocalActivity = useCallback(() => {
    localLastActivityRef.current = Date.now();
    broadcastActivity();
    if (isIdleRef.current) {
      isIdleRef.current = false;
      setIsIdle(false);
    }
  }, [broadcastActivity]);

  const handleRemoteActivity = useCallback((data) => {
    if (!data || data.type !== "SYSTEM_ACTIVITY") return;
    if (typeof data.at !== "number") return;
    if (data.at > remoteLastActivityRef.current) {
      remoteLastActivityRef.current = data.at;
    }
    if (isIdleRef.current) {
      isIdleRef.current = false;
      setIsIdle(false);
    }
  }, []);

  const resetIdleTimer = useCallback(() => {
    const now = Date.now();
    // Assigns unconditionally — safe even during the lazy-init window
    // where these refs may still be null.
    localLastActivityRef.current = now;
    remoteLastActivityRef.current = now;
    if (isIdleRef.current) {
      isIdleRef.current = false;
      setIsIdle(false);
    }
  }, []);

  // Register DOM listeners for local activity.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    events.forEach((evt) => {
      window.addEventListener(evt, updateLocalActivity, { passive: true });
    });

    return () => {
      events.forEach((evt) => {
        window.removeEventListener(evt, updateLocalActivity);
      });
    };
  }, [events, updateLocalActivity]);

  // Periodic check: is system idle?
  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    checkTimerRef.current = setInterval(() => {
      const now = Date.now();
      // Guard against the brief window between mount and the lazy-init
      // effect — refs may be null for the first tick.
      const local = localLastActivityRef.current;
      const remote = remoteLastActivityRef.current;
      if (local == null && remote == null) return;
      const lastSeen = Math.max(local || 0, remote || 0);
      const shouldBeIdle = now - lastSeen >= idleMs;

      if (shouldBeIdle !== isIdleRef.current) {
        isIdleRef.current = shouldBeIdle;
        setIsIdle(shouldBeIdle);
      }
    }, 1000);

    return () => {
      if (checkTimerRef.current) {
        clearInterval(checkTimerRef.current);
        checkTimerRef.current = null;
      }
    };
  }, [idleMs]);

  // Open the BroadcastChannel for SYSTEM_ACTIVITY heartbeats.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (typeof window.BroadcastChannel !== "function") return undefined;

    let channel;
    try {
      channel = new window.BroadcastChannel(channelName);
      channelRef.current = channel;
    } catch {
      // BroadcastChannel can throw in some sandboxed contexts.
      return undefined;
    }

    const listener = (event) => handleRemoteActivity(event.data);
    channel.addEventListener("message", listener);

    return () => {
      channel.removeEventListener("message", listener);
      channel.close();
      channelRef.current = null;
    };
  }, [channelName, handleRemoteActivity]);
  return { isIdle, resetIdleTimer };

};
